import React, { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { SMLDomainSelect } from '../../components/SMLDomainSelect/SMLDomainSelect';
import { splitPlatformDomainPrefix } from '../../utils/platformDomainPrefix';
import { AddressState } from '../../profile/useAccountSlugCheck';
import { useEditorContext } from '../SMLEditorContext';
import { pageMessages as m } from './pageMessages';
import { useSavePageFields } from './useSavePageFields';
import { useSmartLinkSlugCheck } from './useSmartLinkSlugCheck';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for several parts; each part uses its own slice of it */
import styles from './SMLPageTab.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The address of a song page (design a6): the domain and the end of the address, checked while it
 * is typed (Free, Taken with a free address, Checking). A free address saves at once.
 */
const SMLPageAddressCardComponent: React.FC = () => {
  const intl = useIntl();
  const slugId = useId();
  const { smartLink, accountId, smartLinkId, slugPrefix } = useEditorContext();
  const savedSlug = smartLink.recentSmartLinkVersion.slug;
  const [slug, setSlug] = useState(savedSlug);
  const { state, suggestion } = useSmartLinkSlugCheck(slug, accountId, smartLinkId, savedSlug);
  const { save } = useSavePageFields(smartLink);
  const parts = useMemo(() => splitPlatformDomainPrefix(slugPrefix), [slugPrefix]);
  const publishedSlug = smartLink.publishedSmartLinkVersion?.slug;
  // The "old link" note only makes sense when the live address changes (QA finding lit-12): a new
  // page has no old link.
  const changesLiveAddress = publishedSlug !== undefined && slug !== publishedSlug;

  useEffect(() => setSlug(savedSlug), [savedSlug]);
  // A free address is saved; a taken or wrong one waits for a change.
  useEffect(() => {
    if (state === AddressState.free && slug !== savedSlug) {
      save({ slug }).catch(console.error);
    }
  }, [save, savedSlug, slug, state]);

  const onSlug = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setSlug(event.target.value.trim().toLowerCase()),
    []
  );
  const takeSuggestion = useCallback(() => {
    if (suggestion !== undefined) {
      setSlug(suggestion);
    }
  }, [suggestion]);
  const onDomain = useCallback((platformDomain: string) => save({ platformDomain }).catch(console.error), [save]);

  let status: string | null = null;
  if (state === AddressState.checking) {
    status = intl.formatMessage(m.slugChecking);
  } else if (state === AddressState.free) {
    status = intl.formatMessage(m.slugFree);
  } else if (state === AddressState.taken) {
    status = intl.formatMessage(m.slugTaken);
  }
  const isBad = state === AddressState.taken || state === AddressState.illegal;

  return (
    <section className={styles.card} data-testid="page-address-card">
      <h4 className={styles.cardTitle}>{intl.formatMessage(m.address)}</h4>
      {parts !== null && (
        <div className={styles.field}>
          <span className={styles.label}>{intl.formatMessage(m.domain)}</span>
          <SMLDomainSelect value={parts.domain} onChange={onDomain} />
          <p className={styles.help}>{intl.formatMessage(m.domainHelp)}</p>
        </div>
      )}
      <div className={styles.field}>
        <label htmlFor={slugId} className={styles.label}>
          {intl.formatMessage(m.slug)}
        </label>
        <span className={clsx(styles.inputGroup, isBad && styles.inputBad)}>
          <span className={styles.prefix}>{slugPrefix}</span>
          <input id={slugId} className={styles.input} value={slug} onChange={onSlug} data-testid="page-slug" />
          {status !== null && (
            <span className={clsx(styles.status, state === AddressState.free && styles.statusOk)}>{status}</span>
          )}
        </span>
        {state === AddressState.illegal && <p className={styles.helpBad}>{intl.formatMessage(m.slugIllegal)}</p>}
        {state === AddressState.taken && (
          <p className={styles.helpBad}>
            {intl.formatMessage(m.slugTakenHelp)}{' '}
            {suggestion !== undefined && (
              <button type="button" className={styles.link} onClick={takeSuggestion}>
                {intl.formatMessage(m.slugUse, { slug: suggestion })}
              </button>
            )}
          </p>
        )}
      </div>
      {changesLiveAddress && <p className={styles.warn}>{intl.formatMessage(m.slugLiveWarning)}</p>}
    </section>
  );
};

export const SMLPageAddressCard = TypedMemo(SMLPageAddressCardComponent);
